import { useMemo } from 'react';
import { AlgoliaHit } from '~/features/productsList/models';
import { FormattedAlgoliaProductType } from '~/features/productsList/types';
import { mapFromAlgolia } from '~/features/productsList/utils';
import { useFranchise } from '~/shared/utils';
import styles from './product-card-size.module.css';
import { PriceGroup } from '../../price-group/price-group.component';
import { Button } from '../../button/button.component';
import { CMSLink } from '../../cms-link/cms-link.component';
import { Typography } from '../../typography/typography.component';
import ProductImage from '../../product-image/product-image.component';

type OnClickArgs = { productId: string; productColorId: string };
type Props = {
    hit: AlgoliaHit;
    isLoading: boolean;
    onClick: (args: OnClickArgs) => void;
    buttonText?: string;
    imagePriority?: 'Product' | 'Model';
};

export const ProductCardSize = (props: Props) => {
    const franchise = useFranchise();
    const {
        images,
        name,
        priceDetails,
        productId,
        objectId,
        productUrl,
    }: FormattedAlgoliaProductType = useMemo(
        () => mapFromAlgolia(props.hit, franchise, parseInt(props.hit.__position)),
        [props.hit, franchise]
    );
    const image = props.imagePriority
        ? images.find(({ label }) => label?.startsWith(props.imagePriority ?? '')) ?? images?.[0]
        : images?.[0];

    return (
        <div className={styles.root}>
            <CMSLink
                href={productUrl}
                className={styles.imageWrapper}
                onDragStart={(e) => e.preventDefault()}
            >
                <ProductImage image={image} width={48} height={48}/>
            </CMSLink>
            <div className={styles.info}>
                {name && <Typography variant="caption">{name}</Typography>}
                {priceDetails && <PriceGroup variant="overline" {...priceDetails} />}
                <Button
                    size="small"
                    variant="secondary"
                    onClick={() => props.onClick({ productId, productColorId: objectId })}
                    disabled={props.isLoading}
                >
                    {props.buttonText}
                </Button>
            </div>
        </div>
    );
};

ProductCardSize.displayName = 'ProductCardSize';
